<template>
    <div class="autocomplete-container">
        <input
            ref="input"
            v-model="value"
            :placeholder="hint"
            @keyup.enter="submit()"
            @keydown.up.prevent="scroll(-1)"
            @keydown.down.prevent="scroll(1)"
            @input="input"
        >
        <svg
            :class="`search-icon ${value !== '' ? 'hide-if-focus' : ''}`"
            viewbox="0 0 21.02 21.02"
            width="20px"
            height="20px"
            overflow="visible"
        >
            <circle
                class="lens"
                cx="9"
                cy="9"
                r="7.5"
            />
            <line
                class="handle"
                x1="15.36"
                y1="15.36"
                x2="21.02"
                y2="21.02"
            />
        </svg>
        <ul class="autocomplete-content">
            <li
                v-for="(item, i) in getAutocompleteItems(value)"
                ref="items"
                :key="item[valueProp]"
                :class="{
                    option: true,
                    highlight: i === highlightIdx
                }"
                @click="submit(item[valueProp])"
            >
                <span class="value">{{ item[displayProp] }}</span>
            </li>
        </ul>
    </div>
</template>

<script>
import { isIE } from '@/services/helpers';

export default {
    props: {
        getAutocompleteItems: {
            type: Function,
            default: () => []
        },
        displayProp: {
            type: String,
            default: 'value'
        },
        secondaryProp: {
            type: String,
            default: 'secondaryValue'
        },
        valueProp: {
            type: String,
            default: 'value'
        },
        placeholder: {
            type: String,
            required: true,
        }
    },
    data() {
        return {
            value: '',
            highlightIdx: 0
        };
    },
    computed: {
        items() {
            return this.getAutocompleteItems(this.value);
        },
        hint() {
            return this.$screens.mobile ? '' : this.placeholder;
        }
    },
    methods: {
        submit(value) {
            if (value) {
                this.value = value;
            }
            if (this.items.length === 0) {
                this.$emit('submit', this.value);
            } else {
                this.$emit('submit', this.items[this.highlightIdx][this.valueProp]);
                this.$refs.input.blur();
                this.value = '';
            }
        },
        scroll(direction) {
            this.highlightIdx += direction;
            this.highlightIdx = (this.highlightIdx + this.items.length) % this.items.length;

            const el = this.$refs.items[this.highlightIdx];
            if (isIE()) {
                if (el.offsetTop + el.offsetHeight > el.parentElement.scrollTop + el.parentElement.offsetHeight) {
                    el.scrollIntoView(false);
                } else if (el.offsetTop < el.parentElement.scrollTop) {
                    el.scrollIntoView(true);
                }
            } else {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            }
        },
        input() {
            if (this.highlightIdx !== -1) {
                this.highlightIdx = 0;
            }
            this.$emit('input', this.value);
        }
    }
};
</script>

<style lang="postcss" scoped>
div.autocomplete-container {
    @apply relative flex flex-row items-baseline;
}

input {
    @apply
        px-2
        py-1

        my-1
        font-normal
        text-sm

        w-full
        bg-transparent;
}

.autocomplete-content {
    @apply
        absolute
        left-0
        bg-white
        overflow-y-scroll
        z-10;

    top: 100%;
    max-height: 300px;
}

input:not(:focus) ~ .autocomplete-content:not(:hover) {
    @apply opacity-0 pointer-events-none;
}

input:focus ~ .hide-if-focus {
    @apply opacity-0 pointer-events-none;
}

.search-icon {
    @apply absolute right-0 top-0 text-neutral-3 mt-2 mr-1;
    /* top: 0.4em; */
    /* font-size: 1.5em !important; */
}

.search-icon circle.lens {
    @apply stroke-current;
    stroke-width: 3px;
    fill: none;
}

.search-icon line.handle {
    @apply stroke-current;
    stroke-width: 3px;
}

.option {
    @apply
        px-4
        py-2
        cursor-pointer;
}

.option:hover {
    @apply
        bg-neutral-5;
}
.option.highlight {
    @apply
        bg-primary-3
        text-secondary-blue;
}

.value {
    @apply text-xs;
}
.secondary-value {
    @apply text-xs pl-2 text-neutral-3;
}
.option.highlight .secondary-value {
    @apply text-neutral-4;
}
</style>
